import { type FC, type MouseEvent, useCallback } from 'react';

import { useStore } from '@nanostores/react';
import { useParams } from '@tanstack/react-router';
import qs from 'qs';

import type { AssetsAssetSchemaTypedWithId } from '@iconik-io/iconik-api/src/services/assets';
import type { SegmentEntityWithIdNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/segment';
import type { UserRoles } from '@iconik-io/shared-lib/src/utils/roles';
import { useBasePlayerContext } from '@iconik-io/unified-player-ui-react';

import { SUBCLIPS_KEY } from '../../constants/data';
import { SUBCLIP_CREATE_MODAL } from '../../constants/modals';
import { openModal } from '../../ducks/modals_manager/slice';
import {
  selectAllowSubclips,
  selectAsset,
} from '../../ducks/pages/asset/selectors';
import { openPanel } from '../../ducks/panels/slice';
import { highlightSubclip } from '../../ducks/panels/subclips/thunks';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import Button from '../../ui/button/button';
import IconButton from '../../ui/icon_button/icon_button';
import Label from '../../ui/label/label';
import { selectUserRoles } from '../../utils/get_user_roles';
import CopyButton from '../copy_button/copy_button';
import TooltipTrigger from '../tooltip_trigger/tooltip_trigger';

import styles from './segment.module.css';

type Props = {
  activeDrawingSegmentId?: string;
  baseUrl: string;
  latestActiveVersionId?: string;
  isCurrentVersion?: boolean;
  onReply?: (event: MouseEvent) => void;
  segment: SegmentEntityWithIdNormalized;
  replyButtonDisabled?: boolean;
};

const getSegmentLabel = (segment: SegmentEntityWithIdNormalized) => {
  let label: string | undefined;
  if (segment.segment_type === 'COMMENT') {
    label = 'comment';
  }
  if (segment.segment_type === 'GENERIC' || segment.segment_type === 'TAG') {
    label = 'time-based metadata';
  }
  return label;
};

const getCanCreateSubclipFromSegment = (args: {
  assetType?: AssetsAssetSchemaTypedWithId['type'];
  segment: SegmentEntityWithIdNormalized;
  roles: UserRoles;
}) => {
  const { assetType, segment, roles } = args;
  if (!roles.canCreateSubclips || !roles.canCreateAssets) {
    return false;
  }
  if (assetType !== 'ASSET') {
    return false;
  }
  if (!(segment.segment_type === 'GENERIC' || segment.segment_type === 'TAG')) {
    return false;
  }
  if (segment.subclip_id) {
    return false;
  }
  return true;
};

type SubclipButtonProps = { segment: SegmentEntityWithIdNormalized };

const SubclipButton: FC<SubclipButtonProps> = ({ segment }) => {
  const dispatch = useAppDispatch();
  const roles = useAppSelector(selectUserRoles);
  const { versionId } = useParams({ strict: false });
  const allowSubclips = useAppSelector((state) =>
    selectAllowSubclips(state, segment.asset_id, versionId),
  );
  const asset = useAppSelector((state) => selectAsset(state, segment.asset_id));

  const handleNavigateToSubclip = () => {
    dispatch(openPanel(SUBCLIPS_KEY));
    if (segment.subclip_id) {
      dispatch(highlightSubclip([segment.subclip_id]));
    }
  };

  if (!asset || !allowSubclips) {
    return null;
  }
  if (segment.subclip_id) {
    return (
      <IconButton
        iconName="asset_type_subclip"
        onClick={handleNavigateToSubclip}
      />
    );
  }

  const canCreateSubclipFromSegment = getCanCreateSubclipFromSegment({
    assetType: asset.type,
    segment,
    roles,
  });
  if (!canCreateSubclipFromSegment) {
    return null;
  }

  return (
    <Button
      iconName="asset_type_subclip"
      noBorder
      onClick={() => {
        dispatch(
          openModal({
            id: SUBCLIP_CREATE_MODAL,
            contextItems: [asset],
            options: { segment },
          }),
        );
      }}
      variant="default"
      size="s"
    >
      Create subclip from range
    </Button>
  );
};

const SegmentControls: FC<Props> = ({
  baseUrl,
  onReply,
  segment,
  replyButtonDisabled,
  isCurrentVersion,
}) => {
  const { store: unifiedPlayerStore } = useBasePlayerContext();
  const isContextualAnnotationVisible = useStore(
    unifiedPlayerStore.segment.annotation.$isContextualAnnotationVisible,
  );
  const currentUnifiedSegment = useStore(
    unifiedPlayerStore.segment.$currentSegment,
  );

  const currentUnifiedSegmentId = currentUnifiedSegment.id;
  const roles = useAppSelector(selectUserRoles);

  const isSegmentVersionMatchingPlayer =
    segment.version_id === undefined || isCurrentVersion;

  const canReply =
    onReply &&
    isSegmentVersionMatchingPlayer &&
    roles.canCreateSegments &&
    segment.status !== 'DELETED';

  const commentUrl = `${baseUrl}/asset/${segment.asset_id}?${qs.stringify({
    segment_id: segment.id,
  })}`;
  const isCurrentAnnotationVisible =
    currentUnifiedSegmentId === segment.id && isContextualAnnotationVisible;

  const handleAnnotationButtonClick = useCallback(() => {
    unifiedPlayerStore.segment.annotation.setIsContextualAnnotationVisible(
      !isCurrentAnnotationVisible,
    );
  }, [isCurrentAnnotationVisible]);

  return (
    <div className={styles.buttons}>
      {canReply ? (
        <IconButton
          aria-label="Reply to comment"
          iconName="reply"
          onClick={onReply}
          disabled={replyButtonDisabled}
        />
      ) : null}
      <SubclipButton segment={segment} />
      <div className={styles.buttons_right}>
        {segment.has_drawing ? (
          <TooltipTrigger
            tooltipText={
              isCurrentAnnotationVisible ? 'Hide annotation' : 'Show annotation'
            }
            component={IconButton}
            componentProps={{
              iconName: 'annotation_annotation',
              onClick: handleAnnotationButtonClick,
              uiKey: 'annotation-show-hide',
              className: styles.checked,
            }}
          />
        ) : null}
        <CopyButton
          copy={commentUrl}
          text={`Copy link to this ${getSegmentLabel(segment)}`}
          placement="top"
          iconName="link"
          className={styles.link_button}
        />
        {segment.is_internal && (
          <TooltipTrigger tooltipText="Internal comment, only visible to logged in users">
            <Label className={styles.internal_label} variant="light">
              Internal
            </Label>
          </TooltipTrigger>
        )}
      </div>
    </div>
  );
};

export default SegmentControls;
